import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { CalendarEventType, DocumentType } from '@/features/calendar/types/calendar-dto';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import DoorIcon from '@/components/icons/DoorIcon';
import Calendar_04_Icon from '@/components/icons/Calendar_04_Icon';
import MoneyReceiveCircleIcon from '@/components/icons/MoneyReceiveCircleIcon';
import User_02_Icon from '@/components/icons/User_02_Icon';
import PancelEdit_01_Icon from '@/components/icons/PancelEdit_01_Icon';
import ComposeIcon from '@/components/icons/ComposeIcon';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import EPopupElement from '@/components/everridge/popup/EPopupElement';
import EPopupAccordion from '@/components/everridge/popup/EPopupAccordion';
import { useReservationDetailsApi } from '@/features/reservations/hooks/useReservationDetailsApi';
import { useAccommodationsApi } from '@/features/accommodations/hooks/useAccommodationsApi';
import { ReservationConfig } from '@/features/reservations/config/ReservationConfig';
import { cn } from '@/lib/utils';
import { differenceInDays } from 'date-fns';
import EButton from '@/components/everridge/EButton';
import CurrencyValue from '@/features/finances/components/CurrencyValue';

type Props = {
    id?: string;
    documentType?: DocumentType;
    apartmentId?: string;
    startDate?: Date;
    endDate?: Date;
    onEdit: (id: string) => void;
    onClose: () => void;
    isMobile?: boolean;
};

/** Unified booking details modal — calendar `documentId` is always the booking id. */
const BookingDetails: FC<Props> = ({
    id,
    documentType,
    apartmentId,
    startDate,
    endDate,
    onEdit,
    onClose,
    isMobile,
}) => {
    const [isOpen, setIsOpen] = useState(true);
    const [isEdit, setIsEdit] = useState(false);

    const { t } = useTranslation();

    // Accommodations ("sales") can span multiple rooms/periods on one booking, so their details are
    // scoped to the clicked room + period via the accommodation endpoint; everything else uses reservations.
    const isAccommodation =
        documentType === DocumentType.Accommodation && !!apartmentId && !!startDate && !!endDate;

    const { reservationDetailsData, cancelReservation, isCancelingReservation } =
        useReservationDetailsApi({ id: isAccommodation ? undefined : id });

    const { accommodationDetailsData } = useAccommodationsApi({
        accommodationId: isAccommodation ? id ?? '' : '',
        apartmentId: apartmentId ?? '',
        startDate,
        endDate,
    });

    const bookingDetails = isAccommodation
        ? accommodationDetailsData && {
              id: accommodationDetailsData.id,
              apartmentId: accommodationDetailsData.apartmentId,
              apartmentName: accommodationDetailsData.apartmentName,
              eventType: accommodationDetailsData.eventType,
              totalAmount: accommodationDetailsData.totalAmount,
              revenues: accommodationDetailsData.revenues,
              checkInDate: accommodationDetailsData.checkInDate,
              checkOutDate: accommodationDetailsData.checkOutDate,
              guestFullName: accommodationDetailsData.guestFullName,
              remarks: accommodationDetailsData.remarks,
              allowedToEdit: false,
              allowedToCancel: false,
          }
        : reservationDetailsData;

    if (!bookingDetails) return null;

    const {
        label,
        icon: Icon,
        iconColor,
    } = ReservationConfig.getReservationDetails(bookingDetails.eventType);

    const handleClose = () => {
        setIsOpen(false);
        setTimeout(onClose, 300);
    };

    const handleEdit = () => {
        setIsEdit(true);
        if (bookingDetails.apartmentId) {
            onEdit?.(bookingDetails.apartmentId);
        }
    };

    const revenueContent = (
        <div className="pl-7.5 pr-6 flex flex-col gap-2 max-h-80 overflow-y-auto">
            {bookingDetails.revenues?.map(({ date, amount }, index) => (
                <div key={index} className="flex justify-between text-sm">
                    <span className="text-popover-800">{DateTimeHelper.formatDate(date)}</span>
                    <span className="text-popover-800">
                        <CurrencyValue value={amount} asset="calendar" />
                    </span>
                </div>
            ))}
        </div>
    );

    const remarksContent = (
        <div className="px-7.5 flex flex-col gap-2">
            <p className="text-popover-800">{bookingDetails.remarks}</p>
        </div>
    );

    const isActiveCancel =
        bookingDetails.allowedToCancel &&
        !!bookingDetails.checkInDate &&
        differenceInDays(new Date(bookingDetails.checkInDate), new Date()) >= 5;

    const isActiveEdit =
        bookingDetails.allowedToEdit &&
        !!bookingDetails.checkInDate &&
        differenceInDays(new Date(bookingDetails.checkInDate), new Date()) >= 1;

    const titleKey =
        bookingDetails.eventType === CalendarEventType.Accommodation
            ? 'main.reservations.components.details.title2'
            : 'main.reservations.components.details.title1';

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogDescription />
                    <DialogTitle>
                        {t(titleKey)}
                        <DialogClose
                            className={cn(
                                'ml-auto flex items-center justify-center cursor-pointer',
                                isMobile && 'size-11 border border-popover-100 rounded-full',
                            )}
                        >
                            <Cancel_01_Icon />
                        </DialogClose>
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-6 overflow-y-auto overflow-x-hidden select-none">
                    <div className="flex flex-col gap-2">
                        <h4 className="text-sm text-popover-800">
                            {`${DateTimeHelper.formatDate(
                                bookingDetails.checkInDate,
                            )} - ${DateTimeHelper.formatDate(bookingDetails.checkOutDate)}`}
                        </h4>
                        <span className="text-xl font-medium">
                            <CurrencyValue value={bookingDetails.totalAmount} asset="calendar" />
                        </span>
                    </div>
                    <div className="p-3 rounded-semi-xl flex items-center gap-2.5 bg-popover-50 text-sm">
                        <Icon className={iconColor} />
                        <span>{label}</span>
                    </div>
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2 text-sm">
                            <h5 className="text-popover-800">
                                {t('main.reservations.components.details.list.general.title')}
                            </h5>
                            <div>
                                <EPopupElement
                                    icon={DoorIcon}
                                    label={t(
                                        'main.reservations.components.details.list.general.elements.apartment',
                                    )}
                                    value={bookingDetails.apartmentName ?? ''}
                                />
                                <EPopupElement
                                    icon={Calendar_04_Icon}
                                    label={t(
                                        'main.reservations.components.details.list.general.elements.checkIn',
                                    )}
                                    value={DateTimeHelper.formatDate(bookingDetails.checkInDate, 2)}
                                />
                                <EPopupElement
                                    icon={Calendar_04_Icon}
                                    label={t(
                                        'main.reservations.components.details.list.general.elements.checkOut',
                                    )}
                                    value={DateTimeHelper.formatDate(bookingDetails.checkOutDate, 2)}
                                />
                                <EPopupAccordion
                                    icon={MoneyReceiveCircleIcon}
                                    label={t(
                                        'main.reservations.components.details.list.general.elements.revenue',
                                    )}
                                    value={
                                        <CurrencyValue
                                            value={bookingDetails.totalAmount}
                                            asset="calendar"
                                        />
                                    }
                                    content={revenueContent}
                                    componentClassName="py-3 fill-none"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 text-sm">
                            <h5 className="text-popover-800">
                                {t('main.reservations.components.details.list.other.title')}
                            </h5>
                            <div>
                                <EPopupElement
                                    icon={User_02_Icon}
                                    label={t(
                                        'main.reservations.components.details.list.other.elements.guestFullname',
                                    )}
                                    value={bookingDetails.guestFullName ?? ''}
                                />
                                {bookingDetails.eventType === CalendarEventType.HouseUse && (
                                    <EPopupAccordion
                                        icon={PancelEdit_01_Icon}
                                        content={remarksContent}
                                        componentClassName="py-3 fill-none"
                                        value={t(
                                            'main.reservations.components.details.list.other.elements.remarks.value',
                                        )}
                                        label={t(
                                            'main.reservations.components.details.list.other.elements.remarks.title',
                                        )}
                                    />
                                )}
                            </div>
                        </div>
                        {bookingDetails.eventType === CalendarEventType.HouseUse && (
                            <div className="flex gap-4">
                                <EButton
                                    variant={'outline'}
                                    tooltipText={t(
                                        'main.reservations.components.details.buttons.cancelTooltipText',
                                    )}
                                    icon={<Cancel_01_Icon className="stroke-error-1000" />}
                                    className="flex-1 text-error-1000 hover:shadow-none"
                                    onClick={() => id && cancelReservation(id)}
                                    isLoading={isCancelingReservation}
                                    disabled={!isActiveCancel}
                                >
                                    {t('elements.buttons.cancel')}
                                </EButton>
                                <EButton
                                    variant={'outline'}
                                    tooltipText={t(
                                        'main.reservations.components.details.buttons.editTooltipText',
                                    )}
                                    icon={<ComposeIcon />}
                                    className="flex-1 hover:shadow-none"
                                    onClick={handleEdit}
                                    isLoading={isEdit}
                                    disabled={!isActiveEdit}
                                >
                                    {t('elements.buttons.edit')}
                                </EButton>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default BookingDetails;
